import { auth } from '../firebase';

const DEEPSEEK_API_KEY = (process.env.REACT_APP_DEEPSEEK_API_KEY || '').trim();
const DEEPSEEK_MODEL = process.env.REACT_APP_DEEPSEEK_MODEL || 'deepseek-chat';
const DEEPSEEK_URL = 'https://api.deepseek.com/chat/completions';

const GROQ_API_KEY = (process.env.REACT_APP_GROQ_API_KEY || '').trim();
const GROQ_MODEL = 'llama-3.3-70b-versatile';
const GROQ_URL = 'https://api.groq.com/openai/v1/chat/completions';

// Optional Cloud Function proxy (DeepSeek / Vertex) — used when no browser key is set.
const VERTEX_FUNCTION_URL =
  process.env.REACT_APP_GEMINI_FUNCTION_URL ||
  'https://us-central1-gedprep-c8dc2.cloudfunctions.net/geminiChat';

function isAuthErrorMessage(msg = '') {
  const m = String(msg).toLowerCase();
  return (
    m.includes('invalid authentication') ||
    m.includes('unauthenticated') ||
    m.includes('api key not valid') ||
    m.includes('api_key_invalid') ||
    m.includes('permission denied') ||
    m.includes('expected oauth') ||
    m.includes('sign in required') ||
    m.includes('missing authorization')
  );
}

async function getVertexAuthHeader() {
  const user = auth?.currentUser;
  if (!user) throw new Error('Sign in required to use AI features.');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export const SUBJECT_NAMES = {
  math: 'Mathematics',
  science: 'Science',
  'language-arts': 'Language Arts / RLA',
  'social-studies': 'Social Studies',
};

const LECTURE_FORMAT_RULES = `STRICT FORMATTING RULES:
- Use [[Header Text]] for every section heading, each on its own line (e.g., [[Introduction]])
- Use "Step 1:", "Step 2:", "Step 3:" etc. for ALL procedure steps
- Do NOT use markdown (no **, no ##, no *, no bullet dashes -)
- Write in clear, complete prose sentences like a teacher at the whiteboard
- For any comparison or data table, use a LaTeX tabular block on its own lines, not spaced text:
\\begin{tabular}{ll}
Heading one & Heading two \\\\
Value one & Value two \\\\
\\end{tabular}`;

const MATH_LATEX_RULES = `- Use LaTeX for EVERY mathematical expression — NO exceptions:
  * Inline math: $expression$ — e.g., "the value of $x$ is"
  * Display/block equations: $$expression$$ on its own line
  * In worked examples, EVERY calculation step must be written in LaTeX (not plain text numbers/equations)
  * Example step style: Step 1: Start with $$3x + 5 = 20$$
  * NEVER write math in plain text like "3x + 5 = 20" — always use $...$ or $$...$$`;

const EASY_TEACHING_RULES = `- Use simple everyday language a GED beginner can follow
- Prefer short sentences; avoid jargon unless you immediately explain it
- Explain the WHY in plain words before or after each formula step
- Never assume prior advanced knowledge`;

function buildSolvedImageBlock(solvedImageDescription) {
  if (!solvedImageDescription?.trim()) return '';
  return `
[[Teaching From the Worked Example Picture]]
The instructor uploaded a pictorial solved solution. Teach students EXACTLY what is shown using this description:
${solvedImageDescription.trim()}
Reference "as shown in the worked example" and walk through every visible step in order.`;
}

/* ── DeepSeek (primary) + Groq fallback helpers ── */

async function fetchOpenAICompatible(url, apiKey, model, messages, options = {}) {
  if (!apiKey) {
    throw new Error('API key is not configured.');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model,
      messages,
      temperature: options.temperature ?? 0.65,
      max_tokens: options.maxTokens ?? 2048,
      ...(options.jsonMode ? { response_format: { type: 'json_object' } } : {}),
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error?.message || `API error ${response.status}`);
  }

  const data = await response.json();
  return data.choices?.[0]?.message?.content?.trim() || '';
}

async function* streamOpenAICompatible(url, apiKey, model, messages, options = {}) {
  if (!apiKey) {
    throw new Error('API key is not configured.');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model,
      messages,
      temperature: options.temperature ?? 0.65,
      max_tokens: options.maxTokens ?? 2048,
      stream: true,
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error?.message || `API error ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed === 'data: [DONE]') continue;
      if (trimmed.startsWith('data: ')) {
        try {
          const json = JSON.parse(trimmed.slice(6));
          const chunk = json.choices?.[0]?.delta?.content;
          if (chunk) yield chunk;
        } catch {
          // skip malformed SSE lines
        }
      }
    }
  }
}

async function fetchWithDeepSeek(messages, options = {}) {
  return fetchOpenAICompatible(DEEPSEEK_URL, DEEPSEEK_API_KEY, DEEPSEEK_MODEL, messages, options);
}

async function* streamWithDeepSeek(messages, options = {}) {
  yield* streamOpenAICompatible(DEEPSEEK_URL, DEEPSEEK_API_KEY, DEEPSEEK_MODEL, messages, options);
}

async function fetchWithGroq(messages, options = {}) {
  return fetchOpenAICompatible(GROQ_URL, GROQ_API_KEY, GROQ_MODEL, messages, options);
}

async function* streamWithGroq(messages, options = {}) {
  yield* streamOpenAICompatible(GROQ_URL, GROQ_API_KEY, GROQ_MODEL, messages, options);
}

async function fetchWithVertex(messages, options = {}) {
  const authHeader = await getVertexAuthHeader();
  const response = await fetch(VERTEX_FUNCTION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader },
    body: JSON.stringify({ messages, options, stream: false }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || `Gemini (Vertex) error ${response.status}`);
  }

  const data = await response.json();
  return (data.text || '').trim();
}

async function* streamWithVertex(messages, options = {}) {
  const authHeader = await getVertexAuthHeader();
  const response = await fetch(VERTEX_FUNCTION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader },
    body: JSON.stringify({ messages, options, stream: true }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || `Gemini (Vertex) error ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed === 'data: [DONE]') continue;
      if (!trimmed.startsWith('data: ')) continue;
      try {
        const json = JSON.parse(trimmed.slice(6));
        if (json.text) yield json.text;
        if (json.error) throw new Error(json.error);
      } catch (e) {
        if (e instanceof SyntaxError) continue; // malformed SSE line
        throw e;
      }
    }
  }
}

/* ── Primary: DeepSeek → Cloud Function → Groq ── */

async function groqFetch(messages, options = {}) {
  if (DEEPSEEK_API_KEY) {
    try {
      return await fetchWithDeepSeek(messages, options);
    } catch (err) {
      console.warn('[AI] DeepSeek failed — trying fallbacks.', err.message);
    }
  }

  try {
    return await fetchWithVertex(messages, options);
  } catch (err) {
    if (GROQ_API_KEY) {
      console.warn('[AI] Cloud AI failed — falling back to Groq.', err.message);
      return fetchWithGroq(messages, options);
    }
    if (!DEEPSEEK_API_KEY) {
      throw new Error(
        'No AI backend available. Set REACT_APP_DEEPSEEK_API_KEY in .env (preferred), or ensure the Cloud Function / Groq fallback is configured.'
      );
    }
    throw err;
  }
}

/* ── SSE streaming fetch ── */

async function* groqStream(messages, options = {}) {
  if (DEEPSEEK_API_KEY) {
    try {
      yield* streamWithDeepSeek(messages, options);
      return;
    } catch (err) {
      console.warn('[AI] DeepSeek streaming failed — trying fallbacks.', err.message);
    }
  }

  try {
    yield* streamWithVertex(messages, options);
    return;
  } catch (err) {
    if (GROQ_API_KEY) {
      console.warn('[AI] Cloud AI streaming failed — falling back to Groq.', err.message);
      yield* streamWithGroq(messages, options);
      return;
    }
    if (!DEEPSEEK_API_KEY) {
      throw new Error(
        'No AI backend available. Set REACT_APP_DEEPSEEK_API_KEY in .env (preferred), or ensure the Cloud Function / Groq fallback is configured.'
      );
    }
    throw err;
  }
}




﻿/* ── 1. Lecture generation (streaming) — always fresh per student session ── */

function buildPriorLecturesBlock(priorLectures = []) {
  if (!Array.isArray(priorLectures) || priorLectures.length === 0) return '';
  return priorLectures
    .map((lec, i) => {
      const title = lec.title || `Lecture ${i + 1}`;
      const topic = lec.topic ? ` — Topic: ${lec.topic}` : '';
      return `${i + 1}. ${title}${topic}`;
    })
    .join('\n');
}

function buildStandardLecturePrompt({
  subjectName,
  moduleTitle,
  lectureTitle,
  topic,
  needsMath,
  solvedImageDescription,
}) {
  return `Teach a clear, easy-to-understand step-by-step GED lesson for a beginner:

Subject: ${subjectName}
Module: ${moduleTitle}
Lecture: ${lectureTitle}
Topic: ${topic}

REQUIREMENTS:
- Easy language only — a student who struggles with school should still understand
- Minimum 800 words
- At least 2 fully worked examples with EVERY step labeled "Step 1:", "Step 2:", etc.
- In every worked example, write each math/science calculation step in LaTeX ($...$ or $$...$$)
- Teach as if the student is sitting in front of you — explain the WHY behind each step
${buildSolvedImageBlock(solvedImageDescription)}

Use this EXACT structure (include every section):

[[Introduction]]
3-4 simple sentences introducing the topic and why it matters for the GED exam.

[[What You Need to Know]]
Define all key terms and concepts in plain language. Be thorough but simple.

${needsMath ? `[[Key Formula]]
State the main formula(s) in LaTeX display format ($$...$$). Explain each variable clearly in everyday words.

` : ''}[[Step-by-Step Method]]
Label every action as Step 1:, Step 2:, Step 3: etc. Be explicit — assume the student is a complete beginner.${needsMath ? ' Put formulas in LaTeX.' : ''}

[[Worked Example 1]]
State a specific problem, then solve it completely step by step.${needsMath ? ' Every equation in every step MUST be LaTeX.' : ''}

[[Worked Example 2]]
A different problem type — solve fully step by step.${needsMath ? ' Every equation in every step MUST be LaTeX.' : ''}

[[Common Mistakes to Avoid]]
Write 3 specific errors students make. Number them 1. 2. 3.

[[GED Exam Tips]]
Write 3 specific test-taking strategies for this topic. Number them 1. 2. 3.`;
}

function buildRevisionLecturePrompt({
  subjectName,
  moduleTitle,
  lectureTitle,
  topic,
  needsMath,
  priorLectures,
  solvedImageDescription,
}) {
  const priorBlock = buildPriorLecturesBlock(priorLectures);
  const lectureCount = priorLectures?.length || 0;

  return `This is the FINAL lecture in the module — a MODULE REVISION lesson. Your job is to help the student REVISE and consolidate EVERY previous lecture in this module before they take the module quiz / move on.

Subject: ${subjectName}
Module: ${moduleTitle}
Revision lecture title: ${lectureTitle}
${topic ? `Revision focus note: ${topic}` : ''}

Previous lectures to revise (cover ALL of them):
${priorBlock || '(No prior lecture list provided — revise the whole module thematically.)'}

REQUIREMENTS:
- Minimum 1000 words
- This is NOT a brand-new topic lesson. It is a structured REVISION of the ${lectureCount || 'prior'} lecture(s) above.
- Connect ideas across lectures so the student sees the big picture of the module.
- Include quick recall + at least one mixed practice example that pulls from more than one prior lecture.
- Teach as a friendly teacher running a revision class the day before the test.
${buildSolvedImageBlock(solvedImageDescription)}

Use this EXACT structure (include every section):

[[Revision Warm-Up]]
2-3 sentences on why this module matters for the GED and what the student should walk away knowing today.

[[Module Roadmap — What We Learned]]
Briefly walk through each previous lecture in order. For every prior lecture, give:
- The core idea in 2-3 sentences
- One "must remember" takeaway

[[Key Concepts Recap]]
Unite the most important terms/concepts from the whole module. Be clear and exam-focused.
${needsMath ? `
[[Formulas & Tools to Remember]]
List the key formulas / methods from the module in LaTeX ($$...$$). Say when to use each one.
` : ''}
[[Quick Recall Checks]]
Write 4 short oral-style checks the student can answer mentally (mix topics from different lectures). After each, give the correct answer briefly labeled "Answer:".

[[Mixed Practice Example]]
Give ONE worked example that combines ideas from at least two prior lectures. Label every step "Step 1:", "Step 2:", etc.${needsMath ? ' Every equation in every step MUST be LaTeX ($...$ or $$...$$).' : ''}

[[Common Mix-Ups Across the Module]]
List 3 mistakes students make when topics from this module appear together. Number them 1. 2. 3.

[[GED Exam Game Plan]]
Give 3 concrete strategies for answering exam questions from this module. Number them 1. 2. 3.

[[Before You Move On]]
A short pep-talk checklist of what the student should feel confident about after this revision.`;
}

export async function generateLectureContentStream(
  {
    topic,
    subject,
    moduleTitle,
    lectureTitle,
    solvedImageDescription,
    mode = 'lecture',
    priorLectures = [],
  },
  onChunk
) {
  const subjectName = SUBJECT_NAMES[subject] || subject;
  const needsMath = ['math', 'science'].includes(subject);
  const isRevision = mode === 'revision';

  const userContent = isRevision
    ? buildRevisionLecturePrompt({
        subjectName,
        moduleTitle,
        lectureTitle,
        topic,
        needsMath,
        priorLectures,
        solvedImageDescription,
      })
    : buildStandardLecturePrompt({
        subjectName,
        moduleTitle,
        lectureTitle,
        topic,
        needsMath,
        solvedImageDescription,
      });

  const messages = [
    {
      role: 'system',
      content: isRevision
        ? `You are an expert GED teacher running a module revision class. You help students consolidate prior lessons, reconnect ideas, and prepare for the exam — never introduce an unrelated brand-new topic as the main focus.

${EASY_TEACHING_RULES}
${LECTURE_FORMAT_RULES}
${needsMath ? MATH_LATEX_RULES : ''}`
        : `You are an expert GED teacher delivering a live classroom lesson. You teach with patience and clarity using simple language a beginner can understand.

${EASY_TEACHING_RULES}
${LECTURE_FORMAT_RULES}
${needsMath ? MATH_LATEX_RULES : ''}`,
    },
    {
      role: 'user',
      content: userContent,
    },
  ];

  let fullText = '';
  for await (const chunk of groqStream(messages, {
    maxTokens: isRevision ? 4000 : 3500,
    temperature: 0.55,
  })) {
    fullText += chunk;
    onChunk(chunk);
  }

  if (!fullText) throw new Error('No content returned. Please try again.');
  return fullText;
}



/* ── 2. Teacher Q&A (streaming) — short focused answers, not a full lecture ── */

export async function generateChatResponseStream(
  {
    message,
    lectureTitle,
    topic,
    subject,
    chatHistory,
    lectureContent,
    solvedImageDescription,
  },
  onChunk
) {
  const subjectName = SUBJECT_NAMES[subject] || subject;
  const needsMath = ['math', 'science'].includes(subject);

  const messages = [
    {
      role: 'system',
      content: `You are a helpful GED tutor chatting with a student about "${lectureTitle}" (${subjectName}). Topic focus: ${topic || lectureTitle}.

CRITICAL RESPONSE STYLE:
- Answer ONLY what the student asked — do NOT rewrite or regenerate a full lecture
- Keep answers SHORT and focused: usually 3–8 sentences, or up to ~120 words
- For a tiny clarification, 1–3 sentences is enough
- Use simple beginner-friendly language
- If a worked example is needed, give ONE short example with Step 1:, Step 2: (not a whole lesson)
${needsMath ? `${MATH_LATEX_RULES}
- When showing math, use LaTeX ($...$ / $$...$$) in the example steps
- NEVER print formulas as plain broken lines (do not write V then = then length on separate lines)
- Correct formula style example: $$V = l \\times w \\times h$$ or $V = \\pi r^{2} h$` : ''}
- For emphasis use **bold text** (double asterisks), e.g. **Cylinder**
- Do NOT use single-asterisk markdown alone for titles if you can use **bold**
- Do NOT use many [[Section Headers]] — at most one short [[Quick Tip]] if useful
- Do NOT start with a long introduction or module overview
- Be warm and clear

LECTURE CONTEXT (use only as background — do not repeat the whole lecture):
${lectureContent ? lectureContent.slice(0, 2800) : '(Lecture still loading)'}

${solvedImageDescription?.trim()
    ? `LECTURE FIGURES IN ORDER (use these when the student asks about a picture, diagram, or worked example):\n${solvedImageDescription.trim()}`
    : ''}`,
    },
    ...chatHistory.map((m) => ({ role: m.role, content: m.content })),
    { role: 'user', content: message },
  ];

  let fullText = '';
  for await (const chunk of groqStream(messages, { temperature: 0.5, maxTokens: 900 })) {
    fullText += chunk;
    onChunk(chunk);
  }

  return fullText || 'I could not generate a response. Please try again.';
}



/* ── 3. Quiz generation — lecture (6+) or module (5 × lectures) ── */

export async function generateQuiz({
  lectureTitle,
  topic,
  subject,
  lectureContent,
  questionCount = 6,
  mode = 'lecture',
  moduleTitle = '',
  moduleTopics = [],
}) {
  const subjectName = SUBJECT_NAMES[subject] || subject;
  const count = Math.max(1, Math.min(Number(questionCount) || 6, 40));
  const contentSlice = lectureContent ? lectureContent.slice(0, mode === 'module' || mode === 'revision' ? 4500 : 2800) : '';
  const topicsLine =
    Array.isArray(moduleTopics) && moduleTopics.length
      ? `Lectures covered: ${moduleTopics.join('; ')}`
      : '';

  const scopeLine =
    mode === 'module'
      ? `This is a MODULE exit quiz for "${moduleTitle || 'Module'}". Cover material from ALL lectures in the module. Generate exactly ${count} NEW multiple-choice questions (5 per lecture × lecture count).`
      : mode === 'revision'
        ? `This is a MODULE REVISION quiz for "${moduleTitle || lectureTitle}". Cover material from ALL previous lectures in the module (not only the revision lecture title). Generate exactly ${count} NEW multiple-choice questions that help the student revise.`
      : `This is a LECTURE quiz for "${lectureTitle}". Generate exactly ${count} NEW multiple-choice questions from this lecture only.`;

  const text = await groqFetch(
    [
      {
        role: 'user',
        content: `You are a GED exam coach. ${scopeLine}

Subject: ${subjectName}
Lecture: ${lectureTitle || moduleTitle}
Topic: ${topic || lectureTitle || moduleTitle}
${topicsLine}
Content summary: ${contentSlice || 'Use the topic(s) to create relevant GED-level questions.'}

Return ONLY a valid JSON object with NO extra text:
{
  "questions": [
    {
      "question": "Question text?",
      "options": ["Option A", "Option B", "Option C", "Option D"],
      "correct": 0,
      "explanations": [
        "A: explanation of why this is right or wrong",
        "B: explanation of why this is right or wrong",
        "C: explanation of why this is right or wrong",
        "D: explanation of why this is right or wrong"
      ]
    }
  ]
}

Rules:
- Exactly ${count} questions in the "questions" array
- Each question has exactly 4 options
- "correct" is the 0-based index of the correct option
- Explanations must clearly identify the right answer when the student is wrong
- Vary difficulty; stay GED-appropriate`,
      },
    ],
    {
      temperature: 0.65,
      maxTokens: Math.min(8192, 900 + count * 280),
      jsonMode: true,
    }
  );

  try {
    const parsed = JSON.parse(text);
    if (!parsed.questions || !Array.isArray(parsed.questions)) throw new Error('bad format');
    parsed.questions = parsed.questions.slice(0, count);
    return parsed;
  } catch {
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      if (Array.isArray(parsed.questions)) parsed.questions = parsed.questions.slice(0, count);
      return parsed;
    }
    throw new Error('Could not parse quiz. Please try again.');
  }
}

/* ── 4. GED RLA Extended Response scoring (official 3 traits, 0–2 each) ── */

export async function scoreExtendedResponse({ prompt, studentText, wordCount }) {
  const words = Number(wordCount) || 0;
  const text = await groqFetch(
    [
      {
        role: 'user',
        content: `You are a supportive GED RLA Extended Response rater for adult learners (many are non-native English speakers).
Score ONLY the STUDENT ESSAY against the official GED three-trait rubric. Each trait is an integer 0, 1, or 2. Total is 0–6.

INDEPENDENT JUDGING — CRITICAL:
- There is NO answer key. Do NOT compare the student to any model essay, sample answer, or “correct” wording.
- The student may choose EITHER side. A different claim from what you would write is fine if they use passage evidence.
- Do NOT require the same quotes, structure, or phrasing as a teacher exemplar.
- Paraphrase of the passages counts as evidence. Exact quotes are not required.
- Reward a genuine on-topic attempt. This is a high-school equivalency test, not a college essay contest.
- Be a fair GED rater, not a harsh critic. When a trait is between two scores, choose the HIGHER score.

CALIBRATION (use this scale; do not be stingy):
Trait 1 — Argument & evidence
- 2: Clear position about which side is better supported AND uses at least two ideas/details from the passages, with some explanation.
- 1: On-topic with a claim AND at least some reference to the passages (even if thin, one-sided, or mostly summary). MOST real student drafts belong here or at 2.
- 0: ONLY if off-topic, blank/nearly blank, or purely personal opinion with zero link to the passages.

Trait 2 — Development & organization
- 2: Readable beginning–middle–end; points are grouped; some explanation beyond listing facts.
- 1: Some order (even a few paragraphs or a list of related points). Incomplete intro/conclusion is still a 1, not a 0.
- 0: ONLY if the writing is a jumble of unrelated sentences with no visible structure. Short length alone is NOT a 0 if there are a few connected ideas. Under ~300 words may lower 2→1, but do not fail the trait for length.

Trait 3 — Standard English
- 2: Meaning is clear despite occasional grammar/spelling issues. Simple vocabulary is fine.
- 1: Errors are frequent but you can still understand the argument. GED students often score 1 here — that is acceptable.
- 0: ONLY if errors make the essay largely unreadable.

Do NOT deduct for: missing five-paragraph format, missing a counterargument paragraph, informal tone, or not matching a sample essay.

PROMPT / PASSAGES (the only source texts):
${String(prompt || '').slice(0, 8000)}

STUDENT ESSAY (${words} words):
${String(studentText || '').slice(0, 8000)}

Feedback: 1–2 encouraging sentences per trait. Name one strength and one small next step. Do not quote a model answer.

Return ONLY valid JSON with NO extra text:
{
  "trait1": 1,
  "trait2": 1,
  "trait3": 1,
  "feedbackTrait1": "Short student-facing comment",
  "feedbackTrait2": "Short student-facing comment",
  "feedbackTrait3": "Short student-facing comment",
  "wordCount": ${words}
}`,
      },
    ],
    {
      temperature: 0.35,
      maxTokens: 1200,
      jsonMode: true,
    }
  );

  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error('Could not score this response. Please try again.');
    parsed = JSON.parse(jsonMatch[0]);
  }
  return parsed;
}



